import { useAddOnPaymentQuery } from 'data/queries/useAddOnPaymentQuery.hook';
import { BookingAddOnPaymentDto } from '@hqo/resource-booking-client';
import { AddOnCharge } from 'store/api-client/add-on-payment-types';

export interface AddOnPaymentState {
  /** The session billing opened against the linked request, once one exists. */
  checkoutSessionUuid: string | null;
  /**
   * A session is expected for this order and has not arrived yet, so the step holds rather than
   * declaring the order finished. Distinct from "there is nothing to pay": an order of entirely free
   * or quote-required add-ons never gets a session and must not wait for one.
   */
  isAwaitingSession: boolean;
  /**
   * RES-1336 — what was actually charged, once the work order says it was. Null until then, so the
   * confirmation can tell a settled charge from the estimate it has been showing all along.
   */
  charge: AddOnCharge | null;
  /* RES-1317 — the block `addOnReceipt` reads, so this screen can tell a settled charge from one an
   * amend has superseded. */
  payment: BookingAddOnPaymentDto | null;
}

/*
 * RES-1336 — where the add-on charge comes from.
 *
 * The frontend never creates a checkout session. Billing opens one server-side against the linked
 * WORK ORDER once it is priced, and a consumer reads it off that work order — `work-orders-end-user`
 * does nothing cleverer. So this resolves the charge in two hops: resource-booking-service lists the
 * booking's vendor links (#178), and work-orders' tenant read turns a link's `workOrderUuid` into a
 * `checkoutSessionUuid`.
 *
 * ⛔ IT IS NOT ASKED UNTIL AN ORDER EXISTS. The step mounts after the booking does, so an ungated
 * query ran on the picker screen, found no links, cached "nothing to pay" and never re-asked —
 * skipping payment for every order regardless of how the vendor was set up. `isExpected` is the gate.
 *
 * ⛔ IT FAILS OPEN, DELIBERATELY, ON EVERY PATH. A 403 — the caller lacks
 * `CAN_MANAGE_TENANT_WORK_ORDERS` at that building — a `FAILED` link, an erroring read, or a poll
 * that runs out all answer "nothing to pay". By the time this runs the add-ons are ORDERED; only the
 * charge is outstanding, and a vendor can still bill. The cost of giving up is a charge collected
 * another way. The cost of waiting forever is a tenant on a screen with no exit.
 *
 * ⚠️ In practice the 403 should not fire: the step's own quantity-range read
 * (`tenants/current/buildings/:uuid/owner-forms`) sits behind the SAME guard, so a menu that rendered
 * with bounds implies the role is present. That is a reason not to expect it, never a reason to let
 * it hang — which is why it is handled rather than assumed away.
 */
export const useAddOnPayment = (
  buildingUuid: string | null,
  bookingId: number | null,
  /*
   * ⛔ THE CALLER HAS PLACED A PAYABLE ORDER. Nothing is asked until this is true — see the query's
   * own note. While it is false the step has no charge to show, which is the honest answer before an
   * order exists, and the default below says so.
   */
  isExpected: boolean,
  /* The widget reported a successful charge — see the query's own note on why it cannot be inferred. */
  hasCharged = false,
  /*
   * The tenant closed billing's sheet. A weaker second reason to look for a charge, for the ending
   * where the widget took the money but never reported it — see the query's note on the asymmetry
   * between the two, and why only this one is bounded to a single window.
   */
  hasDismissedPayment = false,
  /* RES-1317 -- the pre-submit money fingerprint; see the query's own note. */
  orderedPaymentKey: string | null = null,
): AddOnPaymentState => {
  const { data } = useAddOnPaymentQuery(
    buildingUuid,
    bookingId,
    isExpected,
    hasCharged,
    hasDismissedPayment,
    orderedPaymentKey,
  );

  /* Before the first answer lands, a payable order is still waiting — never "nothing to pay". */
  return data ?? { checkoutSessionUuid: null, isAwaitingSession: isExpected, charge: null, payment: null };
};
